import type { ComponentProps, ReactElement, ReactNode } from "react";
import { useEffect, useState } from "react";

import { Tooltip } from "@similarweb/ui-components";

type TooltipProps = ComponentProps<typeof Tooltip>;
type TooltipBaseProps = Omit<TooltipProps, "content" | "placement" | "open" | "onOpenChange">;
type DualStateTooltipProps = TooltipBaseProps &
    Required<Pick<TooltipProps, "open" | "onOpenChange">> & {
        primaryPlacement: TooltipProps["placement"];
        secondaryPlacement?: TooltipProps["placement"];
        primaryContent: ReactNode;
        secondaryContent?: ReactNode;
    };

const DualStateControlledTooltip = ({
    primaryContent,
    secondaryContent,
    primaryPlacement,
    secondaryPlacement,
    children,
    className,
    open,
    onOpenChange,
    ...props
}: DualStateTooltipProps): ReactElement => {
    const [isShowSecondTooltip, setIsShowSecondTooltip] = useState(false);

    useEffect(() => {
        if (!open || !isShowSecondTooltip) {
            return;
        }

        setIsShowSecondTooltip(false);
    }, [open]);

    if (open || !secondaryContent) {
        return (
            <Tooltip
                showOnFocus
                open={open}
                onOpenChange={onOpenChange}
                content={primaryContent}
                placement={primaryPlacement}
                className={className}
            >
                {children}
            </Tooltip>
        );
    }

    return (
        <Tooltip
            content={secondaryContent}
            placement={secondaryPlacement}
            open={isShowSecondTooltip}
            onOpenChange={(isOpen) => setIsShowSecondTooltip(isOpen)}
            {...props}
        >
            {children}
        </Tooltip>
    );
};

export default DualStateControlledTooltip;
